import { css } from '@emotion/react'
import { Cell, Grid } from 'bold-ui'
import { CheckboxField, DateField, MonthField } from 'components/form'
import { RastreioCancerPerguntaOpcaoEnum } from 'graphql/types.generated'
import { CidadaoAtendimentoContextModel } from 'hooks/atendimento-context/model-atendimentoContext'
import { useField, useForm } from 'react-final-form'
import { dateToReferenceMonth } from 'util/date/converter'
import { toDate } from 'util/date/formatDate'

import { ExamePreventivoRadioField } from './ExamePreventivoRadioField'
import { CitopatologicoPerguntaField } from './model-citopatologico'
import { MotivoExameRadioField } from './MotivoExameRadioField'
import { SangramentoMenopausaRadioField } from './SangramentoMenopausaRadioField'
import { SangramentoRelacaoSexualRadioField } from './SangramentoRelacaoSexualRadioField'
import { SimNaoNaoSabeRadioField } from './SimNaoNaoSabeRadioField'
import { SituacaoEspecialRadioField } from './SituacaoEspecialRadioField'

const IDADE_MAXIMA = 64
const IDADE_MINIMA = 25

interface CitopatologicoAnamneseModalStepProps {
  cidadao: CidadaoAtendimentoContextModel
  serverTime: Date
}

export function CitopatologicoAnamneseModalStep(props: Readonly<CitopatologicoAnamneseModalStepProps>) {
  const { cidadao, serverTime } = props

  const idade = cidadao.idadeEmAnos
  const showSituacaoEspecial = idade < IDADE_MINIMA || idade > IDADE_MAXIMA
  const showNuncaFezPreventivo = idade > IDADE_MAXIMA

  const dataNascimentoAsDate = toDate(cidadao.dataNascimento)

  const form = useForm()

  const {
    input: { value: examePreventivo },
  } = useField<RastreioCancerPerguntaOpcaoEnum>(CitopatologicoPerguntaField.examePreventivo, {
    subscription: { value: true },
  })

  const {
    input: { value: checkDum },
  } = useField<boolean>('checkDum', {
    subscription: { value: true },
  })

  const handleCheckDumChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.target.checked
    if (isChecked) {
      form.change(CitopatologicoPerguntaField.dum, null)
    }
  }

  const handleFezPreventivoChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const novoValor = event.target.value
    if (
      novoValor == RastreioCancerPerguntaOpcaoEnum.NAO ||
      novoValor == RastreioCancerPerguntaOpcaoEnum.NAO_INFORMADO
    ) {
      form.change(CitopatologicoPerguntaField.dataPreventivo, null)
    }
  }

  const showDataPreventivo = examePreventivo === RastreioCancerPerguntaOpcaoEnum.SIM_PAPANICOLAU

  const { positionCheckbox } = createStyles()
  return (
    <Grid>
      <Cell size={12}>
        <MotivoExameRadioField name={CitopatologicoPerguntaField.motivoExame} required />
      </Cell>
      {showSituacaoEspecial && (
        <Cell size={12}>
          <SituacaoEspecialRadioField
            name={CitopatologicoPerguntaField.situacaoEspecial}
            showNuncaFezPreventivo={showNuncaFezPreventivo}
            clearable
          />
        </Cell>
      )}
      <Cell size={12}>
        <ExamePreventivoRadioField
          name={CitopatologicoPerguntaField.examePreventivo}
          onChange={handleFezPreventivoChange}
          required
        />
      </Cell>
      {showDataPreventivo && (
        <Cell size={8}>
          <MonthField
            name={CitopatologicoPerguntaField.dataPreventivo}
            label='Mês e ano em que realizou o último preventivo?'
            required
            minMonth={dateToReferenceMonth(dataNascimentoAsDate) ?? undefined}
            maxMonth={dateToReferenceMonth(serverTime) ?? undefined}
          />
        </Cell>
      )}
      <Cell size={12}>
        <SimNaoNaoSabeRadioField name={CitopatologicoPerguntaField.diu} label='Usa DIU?' clearable />
      </Cell>
      <Cell size={12}>
        <SimNaoNaoSabeRadioField name={CitopatologicoPerguntaField.estaGravida} label='Está gravida?' clearable />
      </Cell>
      <Cell size={12}>
        <SimNaoNaoSabeRadioField
          name={CitopatologicoPerguntaField.usaPilulaAnticoncepcional}
          label='Usa pílula anticoncepcional?'
          required
        />
      </Cell>
      <Cell size={12}>
        <SimNaoNaoSabeRadioField
          name={CitopatologicoPerguntaField.usaHormonio}
          label='Usa hormônio/remédio para tratar a menopausa?'
          required
        />
      </Cell>
      <Cell size={12}>
        <SimNaoNaoSabeRadioField
          name={CitopatologicoPerguntaField.radioterapia}
          label='Fez tratamento de radioterapia?'
          clearable
        />
      </Cell>

      <Cell size={6}>
        <DateField
          name={CitopatologicoPerguntaField.dum}
          label='Data da última menstruação'
          required={!checkDum}
          disabled={checkDum}
          minDate={dataNascimentoAsDate ?? undefined}
          maxDate={serverTime}
        />
      </Cell>
      <Cell size={6} style={positionCheckbox}>
        <CheckboxField name='checkDum' label='Não lembra/não sabe' onChange={handleCheckDumChange} />
      </Cell>

      <Cell size={12}>
        <SangramentoRelacaoSexualRadioField name={CitopatologicoPerguntaField.sangramentoPosRelacao} required />
      </Cell>
      <Cell size={12}>
        <SangramentoMenopausaRadioField name={CitopatologicoPerguntaField.sangramentoPosMenopausa} />
      </Cell>
    </Grid>
  )
}

const createStyles = () => ({
  positionCheckbox: css`
    padding-top: 2.5rem;
    align-self: flex-start;
  `,
})
